import { URL, URLSearchParams } from 'react-native-url-polyfill'
import AsyncStorage from '@react-native-async-storage/async-storage'
import Moment from 'moment'
import Config from 'APP/Config'

import Snowplow from 'APP/Services/Analytics/Snowplow'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { logger } from 'APP/Lib/Datadog'
import { type AppInfo, appInfo } from 'APP/Lib/AppInfo'

export const LAUNCH_CATEGORY = {
  START: 'start launch',
  EXIT: 'exit launch',
}

export const SCROLL_BEHAVIOUR_ACTIONS = {
  SCROLL_OUT_OF_VIEW: 'scroll component out of view',
}

interface EventInfo {
  schema: string
  properties?: Record<string, unknown>
  entities?: string[]
}

export interface Entity {
  schema: string
  data: Record<string, unknown>
}

interface EventPairing {
  [key: string]: EventInfo
}

interface EntityPairing {
  [key: string]: {
    schema: string
    data: Record<string, unknown>
  }
}
export type EventProperties = Record<string, any>

export interface AnalyticsSchemas {
  events: EventPairing
  entities: EntityPairing
}

interface StructuredEventParams {
  category: string
  action: string
  label?: string
  property?: string
  value?: number
}

interface LoadingTimeParams extends Partial<StructuredEventParams> {
  category?: string
  action?: string
}

interface ScrollingBehaviourParams {
  category?: string
  action?: string
  label?: string
}

export const UTM_CONTEXT_KEY = 'utm_context'

interface UtmContext {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_term: string | null
  utm_content: string | null
}

enum BranchIoUtmKeys {
  CAMPAIGN = '~campaign',
  CHANNEL = '~channel',
  FEATURE = '~feature',
}

interface TrackUtmBranchIO {
  props?: Record<string, string | null>
  path?: string
}

interface TrackUtmInitialUrl {
  url?: string | null
}

export enum TrackUtmParamsType {
  URL = 'url',
  BRANCH_IO = 'branchIO',
}

export type TrackUtmParamsProps =
  | { type: TrackUtmParamsType.URL; args: TrackUtmInitialUrl }
  | { type: TrackUtmParamsType.BRANCH_IO; args: TrackUtmBranchIO }

const createURLSearchParams = (url?: string | null): URLSearchParams => {
  return new URLSearchParams(url && url.includes('?') ? url.split('?')[1] : '')
}

export class _Analytics {
  private schemas!: AnalyticsSchemas
  private eventPairing!: EventPairing
  private entityPairing!: EntityPairing
  private activeEntities!: Record<string, Entity>
  private currentId!: string | null
  private launchStartTime!: number | null
  /**
   * TEMPORARY FIX - Race Condition Guard
   * @see https://dialoguemd.atlassian.net/browse/DIA-85713
   *
   * This flag prevents crashes when Redux middleware calls Analytics methods before initialization.
   *
   * PROBLEMS WITH THIS APPROACH:
   * - Silent data loss: Early analytics events are dropped without indication
   * - Masks architectural problem: Race condition still exists, just handled defensively
   * - Difficult to debug: No visibility when events are lost in production
   * - Not future-proof: Any new Analytics method needs similar guards
   *
   * RECOMMENDED SOLUTIONS (in order of preference):
   * 1. Initialize Analytics before Redux store in index.js (simplest)
   * 2. Implement command queue to buffer events until initialization completes
   * 3. Refactor bootstrap to guarantee Analytics initializes before store
   * 4. Decouple middleware from Analytics using Redux saga pattern
   *
   * TODO: Replace this guard with proper initialization order
   */
  private isInitialized: boolean = false

  init(): void {
    this.schemas = Config.ANALYTICS_SCHEMAS
    this.eventPairing = this.schemas.events
    this.entityPairing = this.schemas.entities
    this.activeEntities = {}
    this.currentId = null
    this.launchStartTime = null

    Snowplow.init()

    this.attachEntity('app_info', appInfo)

    Snowplow.setGlobalContexts(
      this.getEntities(['app_info', 'tenant', 'utm_context', 'launch_darkly'])
    )

    this.isInitialized = true
  }

  /**
   * Keep for compatibility
   *
   * This function will be remove in the next PR.
   */
  extractUTMParams = (url?: string | null): UtmContext => {
    const urlSearchParams = createURLSearchParams(url)
    return {
      utm_source: urlSearchParams.get('utm_source'),
      utm_medium: urlSearchParams.get('utm_medium'),
      utm_campaign: urlSearchParams.get('utm_campaign'),
      utm_term: urlSearchParams.get('utm_term'),
      utm_content: urlSearchParams.get('utm_content'),
    }
  }

  /**
   * Checks if the provided UTM parameters indicate a registration link.
   * This is determined by checking the referring link against a list of known registration links.
   * In case of match, we prevent this link to override the existing UTM context.
   * Common registration links include: Auth0 verify email links or SSO callback links.
   */
  isSSORegistrationLink = (trackUtmParamsProps: TrackUtmParamsProps): boolean => {
    try {
      const utmContextRegistrationLink: string[] = Config.UTM_STORAGE_SKIP_DEEP_LINK_URL || []
      const referringLink =
        trackUtmParamsProps.type === TrackUtmParamsType.BRANCH_IO
          ? trackUtmParamsProps.args.props?.['~referring_link']
          : createURLSearchParams(trackUtmParamsProps.args.url).get('~referring_link')

      if (!referringLink) {
        return false
      }

      const url = new URL(referringLink)
      return utmContextRegistrationLink.some((link) => `${url.origin}${url.pathname}` === link)
    } catch (error) {
      logger.error('Error in isSSORegistrationLink', {}, error)
      return false
    }
  }

  extractUTMParamsFromUrl = (url?: string | null): UtmContext => {
    const urlSearchParams = createURLSearchParams(url)

    return {
      utm_source: urlSearchParams.get('utm_source'),
      utm_medium: urlSearchParams.get('utm_medium'),
      utm_campaign: urlSearchParams.get('utm_campaign'),
      utm_term: urlSearchParams.get('utm_term'),
      utm_content: urlSearchParams.get('utm_content'),
    }
  }

  /**
   * Standard `utm_*` params appended to the Branch link take precedence. Branch's own link fields
   * (`~channel`, `~feature`, `~campaign`) are only used when no standard UTM param is present,
   * so a single context never mixes both tagging schemes.
   */
  extractUTMParamsFromBranchIO = (props?: Record<string, string | null>): UtmContext => {
    const standardUtmContext: UtmContext = {
      utm_source: props?.utm_source || null,
      utm_medium: props?.utm_medium || null,
      utm_campaign: props?.utm_campaign || null,
      utm_term: props?.utm_term || null,
      utm_content: props?.utm_content || null,
    }

    if (Object.values(standardUtmContext).some(Boolean)) {
      return standardUtmContext
    }

    return {
      utm_source: props?.[BranchIoUtmKeys.CHANNEL] || null,
      utm_medium: props?.[BranchIoUtmKeys.FEATURE] || null,
      utm_campaign: props?.[BranchIoUtmKeys.CAMPAIGN] || null,
      utm_term: null,
      utm_content: null,
    }
  }

  /**
   * Tracks UTM parameters and manages their storage and association with the current session.
   *
   * This function retrieves the session ID and determines the UTM context based on the type of
   * UTM parameters provided (Branch.io or URL-based). It then checks if the UTM context is empty
   * and handles storage and entity attachment accordingly.
   *
   * - If the UTM context is not empty, it stores the context in AsyncStorage and attaches it as an entity.
   * - If the UTM context is empty, it retrieves the stored UTM context from AsyncStorage and compares
   *   the session ID. If the session ID has changed, it removes the stored UTM context and attaches
   *   an empty context. Otherwise, it attaches the stored UTM context.
   * @returns A promise that resolves when the UTM parameters have been tracked and processed.
   */
  trackUtmParameters = async (trackUtmParamsProps: TrackUtmParamsProps) => {
    try {
      const sessionId = await Snowplow.getSessionId()

      const utmContext =
        trackUtmParamsProps.type === TrackUtmParamsType.BRANCH_IO
          ? this.extractUTMParamsFromBranchIO(trackUtmParamsProps.args.props)
          : this.extractUTMParamsFromUrl(trackUtmParamsProps.args.url)

      const hasEmptyUtmContext = Object.values(utmContext).every((value) => value === null)
      const isRegistrationLink = this.isSSORegistrationLink(trackUtmParamsProps)

      if (!hasEmptyUtmContext && !isRegistrationLink) {
        await AsyncStorage.setItem(UTM_CONTEXT_KEY, JSON.stringify({ sessionId, utmContext }))
        this.attachEntity(UTM_CONTEXT_KEY, utmContext)
      } else {
        const utmContextStoreItem = await AsyncStorage.getItem(UTM_CONTEXT_KEY)
        const { sessionId: sessionIdStored, utmContext: utmContextStored } = utmContextStoreItem
          ? JSON.parse(utmContextStoreItem)
          : {}

        if (sessionIdStored && sessionIdStored !== sessionId) {
          await AsyncStorage.removeItem(UTM_CONTEXT_KEY)
          this.attachEntity(UTM_CONTEXT_KEY, utmContext)
        } else {
          this.attachEntity(UTM_CONTEXT_KEY, utmContextStored)
        }
      }
    } catch (error) {
      logger.error('Error tracking UTM parameters', {}, error)
    }
  }

  getUtmParamsForSignup = (): Record<string, string> => {
    try {
      const utmContext = isWeb()
        ? this.extractUTMParamsFromUrl(typeof window !== 'undefined' ? window.location.search : '')
        : this.activeEntities[UTM_CONTEXT_KEY]?.data

      return Object.fromEntries(
        Object.entries(utmContext ?? {}).filter(([, value]) => value != null && value !== '')
      ) as Record<string, string>
    } catch (error) {
      logger.error('Error reading UTM parameters for signup', {}, error)
      return {}
    }
  }

  extractAdditionalProperties = (eventName: string): EventInfo | undefined =>
    this.eventPairing[eventName]

  getEntities = (entityList: string[]): Entity[] => {
    return Object.entries(this.activeEntities)
      .filter((key) => entityList.includes(key[0]))
      .map((key) => key[1])
  }

  trackEvent = <T extends EventProperties = EventProperties>(
    eventName: string,
    props: T,
    additionalEntities: string[] = []
  ): void => {
    const eventInfo = this.extractAdditionalProperties(eventName)

    if (!eventInfo) {
      logger.error('Attempted to send an unmapped event', { eventName })
      return
    }

    const properties = { ...eventInfo.properties, ...props }

    const trackedEntities = this.getEntities([
      ...(eventInfo.entities || []),
      ...additionalEntities,
      'launch_darkly',
    ])

    Snowplow.trackEvent(eventInfo.schema, properties, trackedEntities)
  }

  identify = (id?: string | null): void => {
    // TEMPORARY FIX: Guard check for race condition
    // @see DIA-85713 - Replace with proper initialization order
    if (!this.isInitialized) {
      return
    }
    if (id && id !== this.currentId && id !== undefined && id !== 'undefined') {
      Snowplow.setSubject(id)
      this.currentId = id
    }
  }

  clearIdentify = (): void => {
    // TEMPORARY FIX: Guard check for race condition
    // @see DIA-85713 - Replace with proper initialization order
    if (!this.isInitialized) {
      return
    }
    Snowplow.setSubject(null)
    this.currentId = null
  }

  trackScreen = (name: string = 'splash', screenType?: string): void => {
    if (isWeb()) {
      if (name) {
        this.trackEvent('navigation', {
          screen_name: name,
        })
      }
    } else {
      Snowplow.trackScreen(
        name,
        screenType,
        this.getEntities(['app_info', 'tenant', 'utm_context', 'launch_darkly'])
      )
    }
  }

  trackLoadingTime = ({
    category = 'Launch time',
    action = 'Action',
    label,
    property = 'time',
    value = Moment().valueOf(),
  }: LoadingTimeParams = {}): void => {
    this.trackStructuredEvent(category, action, label, property, value)
    switch (action) {
      case LAUNCH_CATEGORY.START:
        this.launchStartTime = value
        break
      case LAUNCH_CATEGORY.EXIT:
        this.launchStartTime = null
        break
    }
  }

  launchTimeDiff = (endTime: number): number => {
    if (this.launchStartTime && this.launchStartTime > 0) {
      return endTime - this.launchStartTime
    }
    return 0
  }

  trackScrollingBehaviour = ({
    category = 'Scrolling behaviour',
    action = SCROLL_BEHAVIOUR_ACTIONS.SCROLL_OUT_OF_VIEW,
    label,
  }: ScrollingBehaviourParams = {}): void => {
    this.trackStructuredEvent(category, action, label)
  }

  trackStructuredEvent = (
    category: string,
    action: string,
    label?: string,
    property?: string,
    value?: number
  ): void => {
    Snowplow.trackStructuredEvent(
      { category, action, label, property, value },
      this.getEntities(['app_info', 'tenant', 'utm_context', 'launch_darkly'])
    )
  }

  attachEntity = (
    entityName: string,
    payload: Record<string, unknown> | UtmContext | AppInfo = {}
  ): void => {
    const exploredEntity = this.entityPairing[entityName]
    if (exploredEntity) {
      this.activeEntities[entityName] = {
        schema: exploredEntity.schema,
        data: { ...exploredEntity.data, ...payload },
      }
    } else {
      logger.error('Attempted to attach untracked entity', { entityName })
    }
  }

  detachEntity = (entityName: string): void => {
    if (this.activeEntities[entityName]) {
      delete this.activeEntities[entityName]
    }
  }
}

const Analytics = new _Analytics()

export default Analytics
export type AnalyticsInstance = typeof Analytics
